import {
  Box,
  Container,
  Divider,
  Drawer,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import LeftMenuItemList from "./LeftMenuItemList";
import {
  getTextColor,
  HEADER_HEIGHT,
  LEFT_NAV_WIDTH,
  MenuTabs,
  OrganizationTypes,
  TopBar,
} from "@knowfully/ui.common";
import type { MenuItem as UiMenuItem } from "@knowfully/ui.common/types/commonTypes";
import StartIcon from "@mui/icons-material/Start";
import contextStore from "stores/contextStore";
import { useCustomThemeContext } from "components/App/CustomThemeProvider";
import useGlobalContext from "hooks/globalData";
import ProfileMenu from "layouts/TopBar/ProfileMenu";
import { OrganizationContextStorage } from "utils/organizationContextStorage";
import OrganizationBreadcrumbsWrapper from "components/OrganizationBreadcrumbsWrapper";
import { useIntl } from "react-intl";

const getStyles = (
  theme: Theme,
  hideLeftMenu?: boolean
): { [key: string]: SxProps } => {
  const backgroundColor = theme.palette.background.default;
  const itemTextColor = getTextColor(theme, backgroundColor);
  const selectedItemBackgroundColor = theme.palette.primary.light;
  const selectedItemTextColor = getTextColor(
    theme,
    selectedItemBackgroundColor
  );

  return {
    container: {
      marginTop: HEADER_HEIGHT,
      py: 2,
      [theme.breakpoints.up("lg")]: {
        maxWidth: hideLeftMenu ? undefined : "initial",
      },
    },
    leftPanel: {
      zIndex: 1200,
      mt: HEADER_HEIGHT,
      pb: HEADER_HEIGHT,
      backgroundColor: theme.palette.background.default,
      width: LEFT_NAV_WIDTH,
      transition: "width 750ms ease",
    },
    panelDrawer: {
      width: LEFT_NAV_WIDTH,
      transition: "width 750ms ease",
    },
    collapseMenu: {
      py: 1,
      cursor: "pointer",
      whiteSpace: "nowrap",
      color: itemTextColor,
      "& i": {
        color: itemTextColor,
      },
      "&:hover": {
        color: selectedItemTextColor,
        backgroundColor: selectedItemBackgroundColor,
        "& i": {
          color: selectedItemTextColor,
        },
      },
    },
    informationMessage: {
      fontSize: "1.125rem",
      fontWeight: "bold",
      color: theme.palette.primary.main,
      ml: 5,
    },
    organization: {
      m: 1,
    },
    collapseMenuIcon: {
      transform: "rotate(180deg)",
    },
  };
};

type LeftMenuLayoutProps = {
  hideLeftMenu?: boolean;
};

const LeftMenuLayout = React.memo(({ hideLeftMenu }: LeftMenuLayoutProps) => {
  const theme = useTheme();
  const styles = getStyles(theme, hideLeftMenu);
  const { formatMessage: t } = useIntl();
  const { logoImageUrl, logoAlternativeText } = useCustomThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [collapsed, setCollapsed] = useState(isMobile);
  const { menuService, authenticationService } = useGlobalContext();
  const location = useLocation();
  const { organization } = contextStore.useState();

  const { data: menu } = useQuery(
    ["getLeftMenu"],
    () => menuService.getLeftMenu(),
    {
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  useEffect(() => {
    const contextData = OrganizationContextStorage.get();

    if (contextData) {
      contextStore.update((s) => {
        s.organization = contextData.organization;
      });
    }
  }, []);

  const menuItems = menu?.menuItems ?? [];
  const leftMenuItems = menuItems.filter((i) => !i.parentId);
  const tabItems = menuItems.filter((i) => !!i.parentId) as UiMenuItem[];
  const currentMenuItem = menuItems.find(
    (mi) =>
      !!matchPath(
        { path: mi.url, caseSensitive: false, end: true },
        location.pathname
      )
  );

  const menuTabs = useMemo(() => {
    if (!currentMenuItem) {
      return [];
    }
    return tabItems
      .filter(
        (ti) =>
          ti.parentId === currentMenuItem.parentId ||
          ti.parentId === currentMenuItem.id
      )
      .map((t) => ({
        ...t,
        name: t.name ?? "",
        toolTip: t.toolTip ?? "",
      }));
  }, [currentMenuItem, tabItems]);

  const onStopImpersonationClick = async () => {
    const response = await authenticationService.stopImpersonating();

    if (response.data?.url) {
      window.location.href = response.data.url;
    }
  };

  return (
    <>
      <TopBar
        logoImageUrl={logoImageUrl}
        logoAlternativeText={logoAlternativeText}
        userInfo={menu?.userInfo}
        title={!isMobile && <OrganizationBreadcrumbsWrapper />}
        onStopImpersonationClick={onStopImpersonationClick}
        onMenuClick={() => setCollapsed((prev) => !prev)}
        actions={<ProfileMenu />}
      />
      <Box sx={{ display: "flex" }}>
        {!hideLeftMenu && (
          <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={!collapsed}
            sx={styles.panelDrawer}
            onClose={() => setCollapsed((prev) => !prev)}
            PaperProps={{ sx: styles.leftPanel }}
          >
            {!!isMobile && (
              <>
                <Box sx={styles.organization}>
                  <OrganizationBreadcrumbsWrapper />
                </Box>
                <Divider />
              </>
            )}
            <LeftMenuItemList
              menuItems={leftMenuItems}
              currentMenuItem={currentMenuItem}
              onClick={() => setCollapsed(true)}
            />
            {isMobile && (
              <ListItem
                sx={styles.collapseMenu}
                onClick={() => setCollapsed((prev) => !prev)}
              >
                <ListItemIcon>
                  <StartIcon sx={styles.collapseMenuIcon} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={t({ id: "collapseMenu" })}
                  title={t({ id: "collapseMenu" })}
                />
              </ListItem>
            )}
          </Drawer>
        )}

        <Container component="main" sx={styles.container}>
          {organization?.id &&
          organization.organizationTypeId === OrganizationTypes.Client ? (
            <>
              <MenuTabs tabs={menuTabs} />
              <Outlet />
            </>
          ) : (
            <Typography variant="h6" sx={styles.informationMessage}>
              {t({ id: "pleaseSelectClientOrg" })}
            </Typography>
          )}
        </Container>
      </Box>
    </>
  );
});

export default LeftMenuLayout;
